<template>
  <div class="flex flex-col min-h-screen">
    <div class="flex flex-grow">
      <!-- Mobile sidebar toggle -->
      <TransitionRoot as="template" :show="sidebarOpen">
        <Dialog as="div" class="relative z-40 lg:hidden" @close="sidebarOpen = false">
          <TransitionChild as="template" enter="transition-opacity ease-linear duration-300"
                           enter-from="opacity-0" enter-to="opacity-100"
                           leave="transition-opacity ease-linear duration-300"
                           leave-from="opacity-100" leave-to="opacity-0">
            <div class="fixed inset-0 bg-gray-900/80"/>
          </TransitionChild>
          <div class="fixed inset-0 flex">
            <TransitionChild as="template" enter="transition ease-in-out duration-300 transform"
                             enter-from="-translate-x-full" enter-to="translate-x-0"
                             leave="transition ease-in-out duration-300 transform"
                             leave-from="translate-x-0" leave-to="-translate-x-full">
              <DialogPanel class="relative mr-16 flex w-full max-w-xs flex-1">
                <TransitionChild as="template" enter="ease-in-out duration-300" enter-from="opacity-0"
                                 enter-to="opacity-100" leave="ease-in-out duration-300"
                                 leave-from="opacity-100" leave-to="opacity-0">
                  <div class="absolute left-full top-0 flex w-16 justify-center pt-5">
                    <button type="button" class="-m-2.5 p-2.5" @click="sidebarOpen = false">
                      <span class="sr-only">Close sidebar</span>
                      <XMarkIcon class="h-6 w-6 text-white" aria-hidden="true"/>
                    </button>
                  </div>
                </TransitionChild>
                <div class="flex grow flex-col gap-y-5 overflow-y-auto bg-brand-color px-6 pb-4">
                  <div class="flex h-16 shrink-0 items-center">
                    <span class="text-white text-lg font-bold">Super Admin</span>
                  </div>
                  <nav class="flex flex-1 flex-col">
                    <ul role="list" class="flex flex-1 flex-col gap-y-7">
                      <li>
                        <ul role="list" class="-mx-2 space-y-1">
                          <li v-for="item in navigation" :key="item.name">
                            <nuxt-link :to="item.href" @mousedown="sidebarOpen = false"
                                       :class="[isActive(item.href) ? 'bg-white text-brand-color' : 'text-white/80 hover:text-white hover:bg-white/15',
                                                 'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-medium']">
                              <Icon :name="item.icon" class="h-5 w-5 shrink-0" aria-hidden="true"/>
                              {{ item.name }}
                            </nuxt-link>
                          </li>
                        </ul>
                      </li>
                      <li class="mt-auto">
                        <a @mousedown="onLogout"
                           class="group cursor-pointer -mx-2 flex gap-x-3 rounded-md p-2 text-sm font-medium leading-6 text-white/80 hover:bg-white/15 hover:text-white">
                          <ArrowRightOnRectangleIcon class="h-6 w-6 shrink-0" aria-hidden="true"/>
                          Sign out
                        </a>
                      </li>
                    </ul>
                  </nav>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </Dialog>
      </TransitionRoot>

      <!-- Static sidebar for desktop -->
      <div class="hidden lg:fixed lg:inset-y-0 lg:z-40 lg:flex lg:w-64 lg:flex-col">
        <div class="flex grow flex-col gap-y-5 overflow-y-auto bg-brand-color px-6 pb-4">
          <div class="flex h-16 shrink-0 items-center">
            <span class="text-white text-lg font-bold">Super Admin</span>
          </div>
          <nav class="flex flex-1 flex-col">
            <ul role="list" class="flex flex-1 flex-col gap-y-7">
              <li>
                <ul role="list" class="-mx-2 space-y-1">
                  <li v-for="item in navigation" :key="item.name">
                    <nuxt-link :to="item.href"
                               :class="[isActive(item.href) ? 'bg-white text-brand-color' : 'text-white/80 hover:text-white hover:bg-white/15',
                                         'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-medium']">
                      <Icon :name="item.icon" class="h-5 w-5 shrink-0" aria-hidden="true"/>
                      {{ item.name }}
                    </nuxt-link>
                  </li>
                </ul>
              </li>
              <li class="mt-auto">
                <a @mousedown="onLogout"
                   class="group cursor-pointer -mx-2 flex gap-x-3 rounded-md p-2 text-sm font-medium leading-6 text-white/80 hover:bg-white/15 hover:text-white">
                  <ArrowRightOnRectangleIcon class="h-6 w-6 shrink-0" aria-hidden="true"/>
                  Sign out
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      <!-- Main content -->
      <div class="lg:pl-64 flex-1 overflow-x-hidden">
        <div class="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          <button type="button" class="-m-2.5 p-2.5 text-gray-700 lg:hidden" @click="sidebarOpen = true">
            <span class="sr-only">Open sidebar</span>
            <Bars3Icon class="h-6 w-6" aria-hidden="true"/>
          </button>

          <div class="h-6 w-px bg-gray-900/10 lg:hidden" aria-hidden="true"/>

          <div class="flex flex-1 gap-x-4 self-stretch items-center lg:gap-x-6">
            <div class="relative flex flex-1">
              <span class="font-semibold text-xl text-gray-900">{{ pageTitle }}</span>
            </div>
            <div class="flex items-center gap-x-4 lg:gap-x-6">
              <!-- Profile dropdown -->
              <Menu as="div" class="relative flex-shrink">
                <MenuButton class="-m-1.5 flex items-center">
                  <span class="sr-only">Open menu</span>
                  <ProfilePicture :key="userImage" :name="userName" :image-url="userImage" :size="8"/>
                </MenuButton>
                <transition enter-active-class="transition ease-out duration-100"
                            enter-from-class="transform opacity-0 scale-95"
                            enter-to-class="transform opacity-100 scale-100"
                            leave-active-class="transition ease-in duration-75"
                            leave-from-class="transform opacity-100 scale-100"
                            leave-to-class="transform opacity-0 scale-95">
                  <MenuItems
                      class="absolute right-0 z-10 mt-2.5 w-56 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                    <div class="px-3 pb-2 mb-1 border-b border-gray-100">
                      <p class="text-sm font-semibold text-gray-900 truncate">{{ userName }}</p>
                      <p class="text-xs text-gray-500 truncate">{{ getLoggedInUser?.Email }}</p>
                    </div>
                    <MenuItem v-slot="{ active }">
                      <a @mousedown="goToMyProfile"
                         :class="[active ? 'bg-gray-50' : '', 'flex items-center gap-2 px-3 py-1.5 text-sm leading-6 text-gray-900 cursor-pointer']">
                        <UserCircleIcon class="h-4 w-4 text-gray-500" aria-hidden="true"/>
                        My profile
                      </a>
                    </MenuItem>
                    <MenuItem v-slot="{ active }">
                      <a @mousedown="onLogout"
                         :class="[active ? 'bg-gray-50' : '', 'flex items-center gap-2 px-3 py-1.5 text-sm leading-6 text-gray-900 cursor-pointer']">
                        <ArrowRightOnRectangleIcon class="h-4 w-4 text-gray-500" aria-hidden="true"/>
                        Sign out
                      </a>
                    </MenuItem>
                  </MenuItems>
                </transition>
              </Menu>
            </div>
          </div>
        </div>

        <main class="bg-white min-h-screen">
          <slot/>
        </main>
      </div>
    </div>
  </div>
  <Notification/>
</template>

<script setup lang="ts">
import {ref, computed} from 'vue'
import {
  Dialog, DialogPanel,
  Menu, MenuButton, MenuItem, MenuItems,
  TransitionChild, TransitionRoot,
} from '@headlessui/vue'
import {Bars3Icon, XMarkIcon, ArrowRightOnRectangleIcon, UserCircleIcon} from '@heroicons/vue/24/outline'
const SUPER_ADMIN_PROFILE_ROUTE = '/super-admin/profile';
import {userStore} from "~/store/userStore";
import {companyStore} from "~/store/companyStore";
import {clubStore} from "~/store/clubStore";
import {gradeStore} from "~/store/gradeStore";
import {sessionStore} from "~/store/sessionStore";
import {superAdminStore} from "~/store/superAdminStore";
import {useRoute, useRouter} from "vue-router";
import {storeToRefs} from "pinia";

const useUserStore = userStore();
const useCompanyStore = companyStore();
const route = useRoute();
const router = useRouter();
const {getLoggedInUser} = storeToRefs(useUserStore);

const sidebarOpen = ref(false);

const navigation = [
  {name: 'Dashboard', href: '/super-admin/revenue', icon: 'lucide:layout-dashboard'},
  {name: 'Transfer History', href: '/super-admin/transfers', icon: 'lucide:history'},
  {name: 'Payout Accounts', href: '/super-admin/seller-account', icon: 'lucide:landmark'},
  {name: 'Companies', href: '/super-admin/companies', icon: 'lucide:building-2'},
];

const isActive = (href: string) => {
  return route.path === href || route.path.startsWith(href + '/');
};

const pageTitle = computed(() => {
  const current = navigation.find(item => isActive(item.href));
  if (current) return current.name;
  return isActive(SUPER_ADMIN_PROFILE_ROUTE) ? 'My Profile' : 'Super Admin';
});

const userName = computed(() => {
  const user = getLoggedInUser.value;
  return ((user?.FirstName ?? '') + ' ' + (user?.LastName ?? '')).trim();
});

const userImage = computed(() => getLoggedInUser.value?.profilePicture?.trim() ?? '');

async function goToMyProfile() {
  await router.push(SUPER_ADMIN_PROFILE_ROUTE);
}

async function onLogout() {
  try {
    useUserStore.clearUserStore();
    clubStore().clearClubStore();
    gradeStore().clearGradeStore();
    sessionStore().clearSessionStore();
    useCompanyStore.clearCompanyStore();
    superAdminStore().clearSuperAdminStore();
    await router.push('/aphelia');
  } catch (e) {
    console.log(e);
  }
}

useHead({
  title: 'Super Admin',
  bodyAttrs: {class: 'h-full'},
  htmlAttrs: {class: 'h-full bg-gray-50'}
})
</script>
